import { useFormik } from "formik";
import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Col, Row, Table } from "react-bootstrap";
import TextInput from "../textfield/TextInput";
import { DateSelectorWithoutFormik } from "../textfield/DateSelector";
import { Button } from "../Button";
import CustomPagination from "./Pagination/CustomPagination";
import moment from "moment";
import { Skeleton, TableCell, Tooltip } from "@mui/material";
import { ViewDate } from "../../../utils/DateSupport";
import { searchPatient } from "../../apps/api/Patient";
import ClearIcon from '@mui/icons-material/Clear';

import { failed } from "../Toastify";
import { capitalizeWords } from "../functions";

// Titles for patient list table header
const patientListTitle = [
  "Name",
  "Gender",
  "DOB",
  "Identifier",
  "Address"
];

export default function PatientSelectModal({
  orgId,
  show,
  onHide,
  onPatientSelected,
  initialPatient,
  resetPatient
}) {
  // state
  const [isLoading, setIsLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [isRegion, setIsRegion] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(
    initialPatient?.id || null,
  );
  const [selectedPatientObj, setSelectedPatientObj] = useState(
    initialPatient || null,
  );
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  // patient search formik
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      birthDate: "",
      healthCardNumber: "",
      orgId: orgId,
    },
    validate: (values) => {
      // require at least one field
      if (
        !values.firstName &&
        !values.lastName &&
        !values.birthDate &&
        !values.healthCardNumber
      ) {
        return {
          allSearchFieldsEmpty: "At least one field must be filled out.",
        };
      }
      return {};
    },
    onSubmit: (values) => {
      setBtnLoading(true);
      setIsLoading(true);

      // Fix date/timezone
      const convertDateFormat = (originalDate) => {
        const dateObject = new Date(originalDate);
        const day = String(dateObject.getDate()).padStart(2, "0");
        const month = String(dateObject.getMonth() + 1).padStart(2, "0");
        const year = dateObject.getFullYear();
        return `${month}-${day}-${year}`;
      };

      const timeZoneGuess = moment.tz.guess();
      const timeZoneParts = timeZoneGuess.split("/");
      let region = timeZoneParts[0];
      let dateData = "";
      if (!values.birthDate) {
        dateData = "";
      } else {
        if (
          (region === "America" || region === "Canada") &&
          isRegion === true
        ) {
          let dateDataNew = convertDateFormat(values.birthDate);
          let currentDate = new Date(dateDataNew);
          currentDate.setDate(currentDate.getDate() + 1);
          dateData = convertDateFormat(currentDate);
          setIsRegion(false);
        } else {
          dateData = convertDateFormat(values.birthDate);
        }
      }
      values.birthDate = dateData;

      searchPatient({ ...values, org_id: orgId })
        .then((res) => {
          if (res.status === true) {
            setFilterData(res?.result);
          } else {
            setFilterData([]);
            failed(res.error);
          }
        })
        .catch((res) => {
          failed(
            res?.response?.data?.message ||
              res?.response?.data?.error ||
              res.message,
          );
        })
        .finally(() => {
          setBtnLoading(false);
          setIsLoading(false);
        });
    },
  });

  // Reset form/patients when modal opens/closes
  useEffect(() => {
    if (show) {
        console.log("CLearing state")
      formik.resetForm();
      setFilterData([]);
      setSelectedPatientId(initialPatient?.id || null);
      setSelectedPatientObj(initialPatient || null);
      setPage(0);
    }
    // eslint-disable-next-line
  }, [show]);

  const handleDateChange = (val) => {
    if (val == null || val === "") {
      formik.setFieldValue("birthDate", "");
    }
    const momentDate = moment(val, "MMM-DD-YYYY", true);
    const isValidDate = momentDate.isValid();

    if (isValidDate) {
      formik.setFieldValue(
        "birthDate",
        moment(val).format("MM-DD-YYYY")?.replace(/-/g, "/"),
      );
    } else {
      formik.setFieldValue("birthDate", val);
      formik.setFieldError("birthDate", "Invalid date format");
    }
  };

  const handleClearDate = () => {
    formik.setFieldValue("birthDate", "");
    formik.setFieldError("birthDate", undefined);
    handleDateChange(null);
  };

  const handleSearchPatient = () => {
    resetPatient(null);
    formik.handleSubmit();
  };


  const renderSourceTags = (source) => {
    if (!source) return null;
    const sources = Array.isArray(source) ? source : [source];
    const extSources = sources.filter((src) => src === "EXT");

    if (extSources.length === 0) return null;

    return (
      <>
        {extSources.map((src, index) => (
          <Tooltip
            key={index}
            title="Patient retrieved from external source"
            arrow
          >
            <span
              style={{
                display: "inline-block",
                marginLeft: "8px",
                padding: "2px 6px",
                backgroundColor: "#e0e0e0",
                color: "#666",
                fontSize: "11px",
                borderRadius: "3px",
                fontWeight: "500",
              }}
            >
              {src}
            </span>
          </Tooltip>
        ))}
      </>
    );
  };

  const handlePatientClick = (data) => {
    const baseUrl = window.location.origin;
    const patientId = data?.id;
    const patientName = data?.name?.[0]?.text;
    const url = `${baseUrl}/app/patients-details?patientId=${patientId}`; //&patientName=${patientName}`;
    window.open(url, "_blank");
  };

  // Table rows
  const renderRows = () => {
    if (isLoading) {
      return [...Array(8)].map((_, idx) => (
        <tr key={idx}>
          {Array(8)
            .fill()
            .map((__, i) => (
              <td key={i}>
                <Skeleton animation="wave" />
              </td>
            ))}
        </tr>
      ));
    }
    if (filterData?.length > 0) {
      return filterData
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((data, index) => {
          const formattedDateString = data?.birthDate
            ? ViewDate(data?.birthDate)
            : "-";
          const hcnIdentifier =
            data?.identifier?.find(
              (identifier) => identifier?.type === "HCN",
            ) || {};
          const hcnValue =
            hcnIdentifier && hcnIdentifier?.value ? hcnIdentifier?.value : "-";
          const hcnExternal = data?.identifier?.find(
            (item) => item?.source === "EXT",
          )?.value;

          return (
            <tr key={data?.id || index}>
              <td>
                <input
                  style={{ cursor: "pointer" }}
                  type="radio"
                  name={`radio`}
                  value={data?.id}
                  checked={selectedPatientId === data?.id}
                  onChange={() => {
                    setSelectedPatientId(data?.id);
                    setSelectedPatientObj(data);
                  }}
                />
              </td>
              <td>
                <span
                  style={{ cursor: "pointer" }}
                  onClick={() => handlePatientClick(data)}
                >
                  {data?.name?.[0]?.text
                    ?.split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </span>
                {renderSourceTags(data?.source)}
              </td>
              <td>{capitalizeWords(data?.gender)}</td>
              <td>{formattedDateString}</td>
              <td>{hcnExternal || hcnValue || "-"}</td>
              <td>{data?.address?.[0]?.line?.[0]}</td>
            </tr>
          );
        });
    }
    return (
      <tr>
        <TableCell colSpan="8" style={{ textAlign: "center" }}>
          No Records Found
        </TableCell>
      </tr>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Select Patient</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form
          className="common-form border-fields"
          onSubmit={formik.handleSubmit}
        >
          <Row>
            <Col>
              <TextInput
                keyField={"firstName"}
                label={"First Name"}
                formik={formik}
                placeholder={"First Name"}
                hideRequired={true}
                className="field-wrap text area"
              />
            </Col>
            <Col>
              <TextInput
                keyField={"lastName"}
                label={"Last Name"}
                formik={formik}
                placeholder={"Last Name"}
                hideRequired={true}
              />
            </Col>
            <Col>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div style={{ flex: 1 }}>
                  <DateSelectorWithoutFormik
                    formik={formik}
                    keyField={"birthDate"}
                    label="DOB"
                    value={
                      formik.values.birthDate
                        ? moment(formik.values.birthDate).format("YYYY-MM-DD")
                        : null
                    }
                    handleChange={handleDateChange}
                    isError={
                      formik?.touched?.end && formik?.errors?.end
                        ? formik?.errors?.end
                        : ""
                    }
                    maxDate={moment()}
                    hideRequired={true}
                  />
                </div>
                {formik.values.birthDate && (
                  <Tooltip title="Clear">
                    <ClearIcon
                      style={{
                        color: "#666",
                        cursor: "pointer",
                        fontSize: "20px",
                        alignSelf: "center",
                      }}
                      onClick={handleClearDate}
                    />
                  </Tooltip>
                )}
              </div>
            </Col>
            <Col>
              <TextInput
                keyField={"healthCardNumber"}
                label={"Patient Identifier"}
                formik={formik}
                placeholder={"Patient Identifier"}
                hideRequired={true}
              />
            </Col>
            
            
          </Row>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <Button
              type="submit"
              variant="primary"
              disabled={btnLoading}
              loading={btnLoading}
            >
              {btnLoading ? "Searching..." : "Search"}
            </Button>
          </div>
          {formik?.errors["allSearchFieldsEmpty"] && (
            <div
              style={{
                fontSize: "14px",
                color: "#dc3545",
                paddingTop: "5px",
                marginTop: "-20px",
                marginBottom: "20px",
              }}
            >
              {formik?.errors["allSearchFieldsEmpty"]}
            </div>
          )}
        </form>
        <div className="table-wrap mt-3">
          <Table responsive>
            <thead>
              <tr>
                <th></th>
                {patientListTitle?.map((title, i) => (
                  <th key={i}>{title}</th>
                ))}
              </tr>
            </thead>
            <tbody>{renderRows()}</tbody>
          </Table>
          {filterData?.length > 0 ? (
            <div>
              <CustomPagination
                tableData={filterData}
                totalRows={filterData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                handlePage={(pageNo) => setPage(pageNo)}
              />
            </div>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!selectedPatientId}
          onClick={() => {
            if (selectedPatientObj) {
              onPatientSelected(selectedPatientObj);
            }
          }}
        >
          Select
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
